/**
 * PaymentArrangement Data Model
 * Manages structured repayment plans
 */

// Payment frequency options
export const PAYMENT_FREQUENCIES = {
  WEEKLY: 'weekly',
  BI_WEEKLY: 'bi-weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  ANNUALLY: 'annually'
};

// Payment arrangement validation rules
export const PAYMENT_ARRANGEMENT_VALIDATION_RULES = {
  amount: {
    required: true,
    min: 0.01,
    precision: 2
  },
  frequency: {
    required: true,
    allowedValues: Object.values(PAYMENT_FREQUENCIES)
  },
  nextDueDate: {
    required: true,
    futureDate: true
  },
  settleAmount: {
    required: false,
    min: 0,
    precision: 2
  }
};

/**
 * Creates a new PaymentArrangement object with default values
 * @param {Object} data - Initial payment arrangement data
 * @returns {Object} PaymentArrangement object
 */
export const createPaymentArrangement = (data = {}) => {
  return {
    id: data.id || generatePaymentArrangementId(),
    clientId: data.clientId || '',
    frequency: data.frequency || PAYMENT_FREQUENCIES.MONTHLY,
    amount: roundToTwoDecimals(parseFloat(data.amount) || 0),
    nextDueDate: data.nextDueDate || getNextDueDate(data.frequency || PAYMENT_FREQUENCIES.MONTHLY),
    settleForLess: Boolean(data.settleForLess),
    settleAmount: roundToTwoDecimals(parseFloat(data.settleAmount) || 0),
    arrears: roundToTwoDecimals(parseFloat(data.arrears) || 0),
    isActive: data.isActive !== undefined ? Boolean(data.isActive) : true,
    createdAt: data.createdAt || new Date(),
    updatedAt: data.updatedAt || new Date()
  };
};

/**
 * Calculates the next due date based on frequency
 * @param {string} frequency - Payment frequency
 * @param {Date} fromDate - Starting date (defaults to today)
 * @returns {Date} Next due date
 */
export const getNextDueDate = (frequency, fromDate = new Date()) => {
  const date = new Date(fromDate);
  
  switch (frequency) {
    case PAYMENT_FREQUENCIES.WEEKLY:
      date.setDate(date.getDate() + 7);
      break;
    case PAYMENT_FREQUENCIES.BI_WEEKLY:
      date.setDate(date.getDate() + 14);
      break;
    case PAYMENT_FREQUENCIES.MONTHLY:
      date.setMonth(date.getMonth() + 1);
      break;
    case PAYMENT_FREQUENCIES.QUARTERLY:
      date.setMonth(date.getMonth() + 3);
      break;
    case PAYMENT_FREQUENCIES.ANNUALLY:
      date.setFullYear(date.getFullYear() + 1);
      break;
    default:
      date.setMonth(date.getMonth() + 1); // Default to monthly
  }
  
  return date;
};

/**
 * Calculates arrears for missed payments
 * @param {Object} arrangement - Payment arrangement
 * @param {Array} missedPayments - Array of missed payment dates
 * @returns {number} Total arrears amount
 */
export const calculateArrears = (arrangement, missedPayments = []) => {
  const arrearsAmount = missedPayments.length * arrangement.amount;
  return roundToTwoDecimals(arrearsAmount);
};

/**
 * Updates payment arrangement after a payment is made
 * @param {Object} arrangement - Existing payment arrangement
 * @param {Date} paymentDate - Date payment was made
 * @returns {Object} Updated payment arrangement
 */
export const recordArrangementPayment = (arrangement, paymentDate = new Date()) => {
  const nextDue = getNextDueDate(arrangement.frequency, paymentDate);
  
  return {
    ...arrangement,
    nextDueDate: nextDue,
    arrears: Math.max(0, arrangement.arrears - arrangement.amount), // Reduce arrears if any
    updatedAt: new Date()
  };
};

/**
 * Checks if a payment arrangement is overdue
 * @param {Object} arrangement - Payment arrangement
 * @param {Date} currentDate - Current date (defaults to today)
 * @returns {boolean} True if overdue
 */
export const isArrangementOverdue = (arrangement, currentDate = new Date()) => {
  const dueDate = arrangement.nextDueDate instanceof Date 
    ? arrangement.nextDueDate 
    : new Date(arrangement.nextDueDate);
  return arrangement.isActive && dueDate < currentDate;
};

/**
 * Calculates days until next payment is due
 * @param {Object} arrangement - Payment arrangement
 * @param {Date} currentDate - Current date (defaults to today)
 * @returns {number} Days until due (negative if overdue)
 */
export const getDaysUntilDue = (arrangement, currentDate = new Date()) => {
  const dueDate = arrangement.nextDueDate instanceof Date 
    ? arrangement.nextDueDate 
    : new Date(arrangement.nextDueDate);
  const timeDiff = dueDate.getTime() - currentDate.getTime();
  return Math.ceil(timeDiff / (1000 * 3600 * 24));
};

/**
 * Validates a payment arrangement object
 * @param {Object} arrangement - PaymentArrangement object to validate
 * @returns {Object} Validation result with isValid boolean and errors array
 */
export const validatePaymentArrangement = (arrangement) => {
  const errors = [];
  
  // Check required fields
  if (!arrangement.clientId || String(arrangement.clientId).trim().length === 0) {
    errors.push('Client ID is required');
  }
  
  if (!arrangement.amount || arrangement.amount <= 0) {
    errors.push('Payment amount must be greater than 0');
  }
  
  if (!arrangement.frequency) {
    errors.push('Payment frequency is required');
  }
  
  if (!arrangement.nextDueDate) {
    errors.push('Next due date is required');
  }
  
  // Validate frequency is allowed
  if (arrangement.frequency && !Object.values(PAYMENT_FREQUENCIES).includes(arrangement.frequency)) {
    errors.push('Invalid payment frequency');
  }
  
  // Validate next due date is in the future (for new arrangements)
  if (arrangement.nextDueDate && arrangement.nextDueDate <= new Date() && !arrangement.id) {
    errors.push('Next due date must be in the future');
  }
  
  // Validate numeric fields
  if (arrangement.amount !== undefined && (isNaN(arrangement.amount) || arrangement.amount < 0)) {
    errors.push('Payment amount must be a valid positive number');
  }
  
  if (arrangement.settleAmount !== undefined && (isNaN(arrangement.settleAmount) || arrangement.settleAmount < 0)) {
    errors.push('Settlement amount must be a valid positive number');
  }
  
  // Validate precision (no more than 2 decimal places)
  const numericFields = ['amount', 'settleAmount', 'arrears'];
  numericFields.forEach(field => {
    const value = arrangement[field];
    if (value !== undefined && value !== null && !isNaN(value)) {
      const decimalPlaces = (value.toString().split('.')[1] || '').length;
      if (decimalPlaces > 2) {
        errors.push(`${field} cannot have more than 2 decimal places`);
      }
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Rounds a number to 2 decimal places
 * @param {number} value - Number to round
 * @returns {number} Rounded number
 */
export const roundToTwoDecimals = (value) => {
  return Math.round((value + Number.EPSILON) * 100) / 100;
};

/**
 * Generates a unique payment arrangement ID
 * @returns {string} Unique payment arrangement ID
 */
const generatePaymentArrangementId = () => {
  return 'arrangement_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};